import Dashboard360Service from "@/services/api/dashboard360";
import { handleResponse } from "@/services/axios";
import type {
  AttendanceParams,
  Dashboard360State,
  DealsPipelinesParams,
  ExpenseParams,
  FollowUpsParams,
  GetFeedActivityParams,
  GetTopAbsentees,
  GetTopAbsenteesParams,
  GetTopEmployeeLocationParams,
  GetTopMeetingsParams,
  GetTopTaskViewParam,
  LeadOpportunityParams,
  LeadsAllConversionParams,
  LeadsConversionParams,
  LeadTransactionParams,
  LocationUsersParams,
  LostReasonParam,
  LostReasonListParam,
  OpportunityAllConversionParams,
  OpportunityConversionParams,
  TagsOpportunityParams,
} from "@/types/dashboardtypes";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

/** Dashboard 360 only: API JSON body uses application Status 703 (not an HTTP code). */
function dashboard360MessageIfAccessDenied703(res: unknown): string | null {
  if (res == null || typeof res !== "object") return null;
  const r = res as {
    Status?: number;
    status?: number;
    Message?: string;
    message?: string;
  };
  const st = r.Status ?? r.status;
  if (st !== 703) return null;
  const msg = r.Message ?? r.message;
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return "Access Denied";
}

const initialState: Dashboard360State = {
  isFetching: false,
  feedActivityFetching: false,
  data: {
    topAbsenteesList: [],
    topHalfDaysList: [],
    topExtraDaysList: [],
    topPlannedLeaveList: [],
    topClaimList: [],
    topEmployeeLocationList: [],
    topExpenseList: [],
    topCustomerAddedList: [],
    topCustomerVisitList: [],
    topCustomersDealsList: [],
    topCustomersDealsWonList: [],
    topFiveCustomerByOrder: [],
    dealsAndPipelinesList: [],
    leadsConversionList: [],
    opportunityConversionList: [],
    lostReasonList: [],
    dealsLostReasonLists:[],
    tagsAgainstOpportunityList: [],
    tagsOpportunityList: [],
    tagsOpportunityLossList: [],
    allOpportunitiesByTagList: [],
    wonOpportunitiesByTagList: [],
    lostOpportunitiesByTagList: [],
    opportunityByLeadList: [],
    allOpportunityByLeadSourceList: [],
    leadAccountList: [],
    leadsOnAccountStageByLeadSourceList: [],
    wonOpportunitiesList: [],
    expenseWeekList: [],
    individualTaskList: [],
    teamWiseTaskList: [],
    salesPerformanceList: [],
    sale360ViewList: [],
    teamTask360ViewList: [],
    customerBySourceList: [],
    customerByStatusList: [],
    customerByStageList: [],
    productOrderStatusList: [],
    followUpsList: [],
    topCallByList: [],
    topCallToList: [],
    bottomCallByList: [],
    bottomCallToList: [],
    topProductList: [],
    topMeetingsList: [],
    topPastMeetingsList: [],
    taskViewList: [],
    individualTaskViewList: [],
    feedActivityList: [],
    allAbsenteesList: [],
    allhalfDayList: [],
    allExtraDaysList: [],
    allPlannedLeaves: [],
    allClaimUpdates: [],
    usersByLocationList: [],
    allExpenseList: [],
    customerAddedList: [],
    customerVisitList: [],
    customerDealList: [],
    customerDealsWonList: [],
    customerOrderList: [],
    upcomingMeetingList: [],
    pastMeetingList: [],
    productList: [],
    orderStatusViewList: [],
    leadBySourceList: [],
    leadByStageList: [],
    leadByStatusList: [],
    topCallByListAll: [],
    topCallToListAll: [],
    bottomCallByListAll: [],
    bottomCallToListAll: [],
    dealsAndPipelinesListAll: [],
    leadConversionAllCustomersList: [],
    convertedLeadsList: [],
    convertedOpportunityList: [],
    allOpportunityList: [],
    leadTransactionList: null,
    leadStatusList: [],
    visitPurposesList: [],
    metricsData: null,
    opportunitymetricsData: null,
    ordermetricsData: null,
    mostOrderedProductList: null,
    taskMetricsData: null,
    referralList: null,
    callDetailsViewList: [],
    leadOpportunityCount:[],
    leadOpportunityReport:[],
  },
  error: undefined,
};

// Thunk
export const getTopAbsentees = createAsyncThunk<
  GetTopAbsentees[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/getTopAbsentees", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopAbsentees(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getTopHalfDays = createAsyncThunk<
  GetTopAbsentees[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/getTopHalfDays", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopHalfDays(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getTopExtraDays = createAsyncThunk<
  GetTopAbsentees[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/getTopExtraDays", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopExtraDays(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getTopPlannedLeaves = createAsyncThunk<
  GetTopAbsentees[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/TopUpcomingLeaves", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopPlannedLeaves(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getTopClaims = createAsyncThunk<
  GetTopAbsentees[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/TopClaims", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopClaims(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getEmployeeCountByLocation = createAsyncThunk<
  GetTopAbsentees[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>(
  "dashboard360/EmployeeCountByLocation",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getEmployeeCountByLocation(
        params
      );
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
export const getTopExpense = createAsyncThunk<
  GetTopAbsentees[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/topExpenseList", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopExpense(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getAddedCustomer = createAsyncThunk<
  GetTopAbsentees[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/TopCustomer", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAddedCustomer(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getTopCustomerByOrder = createAsyncThunk<
  GetTopAbsentees[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/TopCustomersOrders", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopCustomerByOrder(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getDealsAndPipelines = createAsyncThunk<
  any[],
  DealsPipelinesParams,
  { rejectValue: string }
>("dashboard360/DealStagePipeline", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getDealsAndPipelines(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getLeadsConversion = createAsyncThunk<
  any[],
  LeadsConversionParams,
  { rejectValue: string }
>("dashboard360/leadCount", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getLeadsConversion(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getOpportunityConversion = createAsyncThunk<
  any[],
  OpportunityConversionParams,
  { rejectValue: string }
>("dashboard360/OpportunityCount", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getOpportunityConversion(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getLostReason = createAsyncThunk<
  any[],
  LostReasonParam,
  { rejectValue: string }
>(
  "dashboard360/OpportunityReasonCount",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getLostReason(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
export const getLostReasonLists = createAsyncThunk<
  any[],
  LostReasonListParam,
  { rejectValue: string }
>(
  "dashboard360/OpportunityReasonList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getLostReasonLists(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
export const getTagsOpportunity = createAsyncThunk<
  any[],
  TagsOpportunityParams,
  { rejectValue: string }
>(
  "dashboard360/GetTagsAgainstOpportunity",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getTagsOpportunity(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);
export const getTagsWonOpportunity = createAsyncThunk<
  any[],
  TagsOpportunityParams,
  { rejectValue: string }
>(
  "dashboard360/GetTagsAgainstWonOpportunity",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getTagsWonOpportunity(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
export const getTagsLossOpportunity = createAsyncThunk<
  any[],
  TagsOpportunityParams,
  { rejectValue: string }
>(
  "dashboard360/GetTagsAgainstLostOpportunity",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getTagsLossOpportunity(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const GetAllOpportunitiesByTag = createAsyncThunk<
  any[],
  any,
  { rejectValue: string }
>("dashboard360/GetAllOpportunitiesbyTag", async (params, { rejectWithValue }) => {
  try {
    const response = await (Dashboard360Service as any).getAllOpportunitiesByTag(
      params,
    );
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const GetAllWonOpportunitiesByTag = createAsyncThunk<
  any[],
  any,
  { rejectValue: string }
>("dashboard360/GetAllWonOpportunitiesbyTag", async (params, { rejectWithValue }) => {
  try {
    const response = await (Dashboard360Service as any).getAllWonOpportunitiesByTag(
      params,
    );
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const GetAllLostOpportunitiesByTag = createAsyncThunk<
  any[],
  any,
  { rejectValue: string }
>("dashboard360/GetAllLostOpportunitiesbyTag", async (params, { rejectWithValue }) => {
  try {
    const response = await (Dashboard360Service as any).getAllLostOpportunitiesByTag(
      params,
    );
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getOpportunityByLead = createAsyncThunk<
  any[],
  TagsOpportunityParams,
  { rejectValue: string }
>(
  "dashboard360/OpportunityByLeadSource",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getOpportunityByLead(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
export const GetAllOpportunityByLeadSource = createAsyncThunk<
  any[],
  any,
  { rejectValue: string }
>(
  "dashboard360/GetAllOpportunityByLeadSource",
  async (params, { rejectWithValue }) => {
    try {
      const response = await (Dashboard360Service as any).getAllOpportunityByLeadSource(
        params,
      );
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);
export const getLeadAccount = createAsyncThunk<
  any[],
  TagsOpportunityParams,
  { rejectValue: string }
>("dashboard360/LeadsOnAccountStage", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getLeadAccount(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllLeadsOnAccountStageByLeadSource = createAsyncThunk<
  any[],
  any,
  { rejectValue: string }
>(
  "dashboard360/GetAllLeadsOnAccountStageByLeadSource",
  async (params, { rejectWithValue }) => {
    try {
      const response = await (
        Dashboard360Service as any
      ).getAllLeadsOnAccountStageByLeadSource(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);
export const GetAllWonOpportunities = createAsyncThunk<
  any[],
  any,
  { rejectValue: string }
>("dashboard360/GetAllWonOpportunities", async (params, { rejectWithValue }) => {
  try {
    const response = await (Dashboard360Service as any).getAllWonOpportunities(
      params,
    );
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getExpenseByWeek = createAsyncThunk<
  any[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/ExpenseByWeek", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getExpenseByWeek(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllIndiviualTask = createAsyncThunk<
  any[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/GetAllIndiviualTask", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllIndividualTask(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllTeamWiseTask = createAsyncThunk<
  any[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/GetAllTeamWiseTask", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllTeamWiseTask(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetSalePerformance = createAsyncThunk<
  any[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/SalePerformance", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getSalePerformance(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const GetSale360View = createAsyncThunk<any, any, { rejectValue: string }>(
  "dashboard360/GetSale360View",
  async (params, { rejectWithValue }) => {
    try {
      const response = await (Dashboard360Service as any).getSale360View(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const GetTeamTask360View = createAsyncThunk<any, any, { rejectValue: string }>(
  "dashboard360/GetTeamTask360View",
  async (params, { rejectWithValue }) => {
    try {
      const response = await (Dashboard360Service as any).getTeamTask360View(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const GetIndividualTaskView = createAsyncThunk<any, any, { rejectValue: string }>(
  "dashboard360/GetIndividualTaskView",
  async (params, { rejectWithValue }) => {
    try {
      const response = await (Dashboard360Service as any).getIndividualTaskView(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const getcustomerBySource = createAsyncThunk<
  any[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/CustomerBySource", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getCustomerBySource(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getcustomerByStatus = createAsyncThunk<
  any[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/CustomerByStatus", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getCustomerByStatus(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getcustomerByStage = createAsyncThunk<
  any[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/CustomerByStage", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getCustomerByStage(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetProductOrderStatus = createAsyncThunk<
  any[],
  TagsOpportunityParams,
  { rejectValue: string }
>("dashboard360/GetProductOrderStatus", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getProductOrderStatus(params);
    const denied = dashboard360MessageIfAccessDenied703(response);
    if (denied) return rejectWithValue(denied);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetFollowUps = createAsyncThunk<
  any[],
  FollowUpsParams,
  { rejectValue: string }
>("dashboard360/GetFollowUps", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getFollowUps(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetTopFiveCallBy = createAsyncThunk<
  any[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/TopCallBy", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopFiveCallBy(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetTopFiveCallTo = createAsyncThunk<
  any[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/TopCallTo", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopFiveCallTo(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetBottomFiveCallBy = createAsyncThunk<
  any[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/BottomCallBy", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getBottomFiveCallBy(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetBottomFiveCallTo = createAsyncThunk<
  any[],
  GetTopEmployeeLocationParams,
  { rejectValue: string }
>("dashboard360/BottomCallTo", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getBottomFiveCallTo(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetTopProduct = createAsyncThunk<
  any[],
  GetTopAbsenteesParams,
  { rejectValue: string }
>("dashboard360/TopProduct", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopProduct(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetTopMeetings = createAsyncThunk<
  any[],
  GetTopMeetingsParams,
  { rejectValue: string }
>("dashboard360/TopMeetings", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTopMeetings(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllListViewTask = createAsyncThunk<
  any[],
  GetTopTaskViewParam,
  { rejectValue: string }
>("dashboard360/GetAllListViewTask", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllListViewTask(params);
    const denied = dashboard360MessageIfAccessDenied703(response);
    if (denied) return rejectWithValue(denied);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetFeedActivityLrm = createAsyncThunk<
  any[],
  GetFeedActivityParams,
  { rejectValue: string }
>("dashboard360/GetFeedActivityLrm", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getFeedActivityLrm(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllAttendanceData = createAsyncThunk<
  any[],
  AttendanceParams,
  { rejectValue: string }
>("dashboard360/AllAttendanceData", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllAttendanceData(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllLocationUsers = createAsyncThunk<
  any[],
  LocationUsersParams,
  { rejectValue: string }
>("dashboard360/AllLocationByUsers", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllLocationUsers(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllExpense = createAsyncThunk<
  any[],
  ExpenseParams,
  { rejectValue: string }
>("dashboard360/GetAllExpense", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllExpense(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const GetAllCustomerViewReport = createAsyncThunk<
  any[],
  AttendanceParams,
  { rejectValue: string }
>(
  "dashboard360/GetAllCustomerViewReport",
  async (params, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getAllCustomerViewReport(
        params
      );
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
export const GetAllCall = createAsyncThunk<
  any[],
  AttendanceParams,
  { rejectValue: string }
>("dashboard360/GetAllCall", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllCall(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetAllDealPipeline = createAsyncThunk<
  any[],
  DealsPipelinesParams,
  { rejectValue: string }
>("dashboard360/GetAllDealPipeline", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getAllDealPipeline(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetLeadConversionAllCustomers = createAsyncThunk<
  any[],
  LeadsAllConversionParams,
  { rejectValue: string }
>(
  "dashboard360/LeadConversionAllCustomer",
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await Dashboard360Service.getLeadConversionAllCustomer(params);
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);
export const GetConvertedLeads = createAsyncThunk<
  any[],
  LeadsAllConversionParams,
  { rejectValue: string }
>("dashboard360/ConvertedLeads", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getConvertedLeads(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const GetConvertedOpportunity = createAsyncThunk<
  any[],
  OpportunityAllConversionParams,
  { rejectValue: string }
>("dashboard360/ConvertedOpportunity", async (params, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getConvertedOpportunity(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const GetAllOpportunity = createAsyncThunk<
  any[],
  OpportunityAllConversionParams,
  { rejectValue: string }
>("dashboard360/AllOpportunity", async (params, { rejectWithValue }) => {
  try {
    const response = await (Dashboard360Service as any).getAllOpportunity(params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getLeadTransaction = createAsyncThunk<
  any[],
  LeadTransactionParams,
  { rejectValue: string }
>("dashboard360/LeadTransaction", async (body, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getLeadTransaction(body);
    return (response as any)?.data ?? response ?? {};
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getLeadStatus = createAsyncThunk<
  any[],
  void,
  { rejectValue: string }
>("dashboard360/Lead_Status_Values", async (_, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getLeadStatus();
    return (response as any)?.data ?? response ?? {};
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});
export const getVisitPurposes = createAsyncThunk<
  any[],
  void,
  { rejectValue: string }
>("dashboard360/VisitPurposes", async (_, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getVisitPurposes();
    return (response as any)?.data ?? response ?? {};
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getVisitsMetrics = createAsyncThunk<
  any,
  { id: string | number; params: any },
  { rejectValue: string }
>("dashboard360/VisitsMetrics", async ({ id, params }, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getVisitsMetrics(id, params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error?.message ?? "Failed to fetch Visit Metrics");
  }
});
export const getOpportunityMetrics = createAsyncThunk<
  any,
  { id: string | number; params: any },
  { rejectValue: string }
>(
  "dashboard360/OpportunityMetrics",
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getOpportunityMetrics(
        id,
        params
      );
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(
        error?.message ?? "Failed to fetch Opportunity Metrics"
      );
    }
  }
);
export const getOrderMetrics = createAsyncThunk<
  any,
  { id: string | number; params: any },
  { rejectValue: string }
>("dashboard360/OrderMetrics", async ({ id, params }, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getOrderMetrics(id, params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error?.message ?? "Failed to fetch Order Metrics");
  }
});
export const getMostOrderedProducts = createAsyncThunk<
  any,
  { id: string | number; params: any },
  { rejectValue: string }
>(
  "dashboard360/MostOrderedProducts",
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getMostOrderedProducts(
        id,
        params
      );
      return (response as any)?.data ?? response ?? [];
    } catch (error: any) {
      return rejectWithValue(
        error?.message ?? "Failed to fetch Most Ordered Products"
      );
    }
  }
);
export const getTaskMetrics = createAsyncThunk<
  any,
  { id: string | number; params: any },
  { rejectValue: string }
>("dashboard360/TaskMetrics", async ({ id, params }, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getTaskMetrics(id, params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) {
    return rejectWithValue(error?.message ?? "Failed to fetch Task Metrics");
  }
});
export const getReferralBussiness = createAsyncThunk<
  any,
  { id: string | number; params: any },
  { rejectValue: string }
>("dashboard360/ReferalBussiness", async ({ id, params }, { rejectWithValue }) => {
  try {
    const response = await Dashboard360Service.getReferralBussiness(id, params);
    return (response as any)?.data ?? response ?? [];
  } catch (error: any) { 
    return rejectWithValue(error?.message ?? "Failed to fetch Refferal Bussiness");
  }
});

export const getLeadOpportunityCount = createAsyncThunk(
  "dashboard360/getLeadOpportunityCount",
  async (params: LeadOpportunityParams, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getLeadOpportunityCount(params);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to fetch lead opportunity count"
      );
    }
  }
);

export const getLeadOpportunityReport = createAsyncThunk(
  "dashboard360/getLeadOpportunityReport",
  async (params: LeadOpportunityParams, { rejectWithValue }) => {
    try {
      const response = await Dashboard360Service.getLeadOpportunityReport(params);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch lead opportunity count"
      );
    }
  }
);

// Slice
const dashboard360Slice = createSlice({
  name: "dashboard360",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTopAbsentees.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTopAbsentees.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topAbsenteesList = action.payload;
      })
      .addCase(getTopAbsentees.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTopHalfDays.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTopHalfDays.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topHalfDaysList = action.payload;
      })
      .addCase(getTopHalfDays.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTopExtraDays.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTopExtraDays.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topExtraDaysList = action.payload;
      })
      .addCase(getTopExtraDays.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTopPlannedLeaves.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTopPlannedLeaves.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topPlannedLeaveList = action.payload;
      })
      .addCase(getTopPlannedLeaves.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTopClaims.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTopClaims.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topClaimList = action.payload;
      })
      .addCase(getTopClaims.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getEmployeeCountByLocation.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getEmployeeCountByLocation.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topEmployeeLocationList = action.payload;
      })
      .addCase(getEmployeeCountByLocation.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTopExpense.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTopExpense.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topExpenseList = action.payload;
      })
      .addCase(getTopExpense.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getAddedCustomer.pending, (state) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getAddedCustomer.fulfilled, (state, action) => {
        state.isFetching = false;
        const { dataFor } = action.meta.arg;

        switch (dataFor) {
          case "CustomerAdded":
            state.data.topCustomerAddedList = action.payload;
            break;
          case "CustomerVisit":
            state.data.topCustomerVisitList = action.payload;
            break;
          case "CustomerDeal":
            state.data.topCustomersDealsList = action.payload;
            break;
          case "CustomersDealsWon":
            state.data.topCustomersDealsWonList = action.payload;
            break;
          default:
            break;
        }
      })
      .addCase(getAddedCustomer.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTopCustomerByOrder.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTopCustomerByOrder.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topFiveCustomerByOrder = action.payload;
      })
      .addCase(getTopCustomerByOrder.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getDealsAndPipelines.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getDealsAndPipelines.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.dealsAndPipelinesList = action.payload;
      })
      .addCase(getDealsAndPipelines.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getLeadsConversion.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLeadsConversion.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.leadsConversionList = action.payload;
      })
      .addCase(getLeadsConversion.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getOpportunityConversion.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getOpportunityConversion.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.opportunityConversionList = action.payload;
      })
      .addCase(getOpportunityConversion.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getLostReason.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLostReason.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.lostReasonList = action.payload;
      })
      .addCase(getLostReason.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getLostReasonLists.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLostReasonLists.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.dealsLostReasonLists = action.payload;
      })
      .addCase(getLostReasonLists.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTagsOpportunity.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTagsOpportunity.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.tagsAgainstOpportunityList = action.payload;
      })
      .addCase(getTagsOpportunity.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTagsWonOpportunity.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTagsWonOpportunity.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.tagsOpportunityList = action.payload;
      })
      .addCase(getTagsWonOpportunity.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTagsLossOpportunity.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTagsLossOpportunity.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.tagsOpportunityLossList = action.payload;
      })
      .addCase(getTagsLossOpportunity.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllOpportunitiesByTag.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllOpportunitiesByTag.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.allOpportunitiesByTagList = action.payload;
      })
      .addCase(GetAllOpportunitiesByTag.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllWonOpportunitiesByTag.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllWonOpportunitiesByTag.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.wonOpportunitiesByTagList = action.payload;
      })
      .addCase(GetAllWonOpportunitiesByTag.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllLostOpportunitiesByTag.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllLostOpportunitiesByTag.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.lostOpportunitiesByTagList = action.payload;
      })
      .addCase(GetAllLostOpportunitiesByTag.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getOpportunityByLead.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getOpportunityByLead.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.opportunityByLeadList = action.payload;
      })
      .addCase(getOpportunityByLead.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllOpportunityByLeadSource.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllOpportunityByLeadSource.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.allOpportunityByLeadSourceList = action.payload;
      })
      .addCase(GetAllOpportunityByLeadSource.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getLeadAccount.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLeadAccount.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.leadAccountList = action.payload;
      })
      .addCase(getLeadAccount.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllLeadsOnAccountStageByLeadSource.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllLeadsOnAccountStageByLeadSource.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.leadsOnAccountStageByLeadSourceList = action.payload;
      })
      .addCase(GetAllLeadsOnAccountStageByLeadSource.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllWonOpportunities.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllWonOpportunities.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.wonOpportunitiesList = action.payload;
      })
      .addCase(GetAllWonOpportunities.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getExpenseByWeek.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getExpenseByWeek.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.expenseWeekList = action.payload;
      })
      .addCase(getExpenseByWeek.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllIndiviualTask.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllIndiviualTask.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.individualTaskList = action.payload;
      })
      .addCase(GetAllIndiviualTask.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllTeamWiseTask.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllTeamWiseTask.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.teamWiseTaskList = action.payload;
      })
      .addCase(GetAllTeamWiseTask.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetSalePerformance.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetSalePerformance.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.salesPerformanceList = action.payload;
      })
      .addCase(GetSalePerformance.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetSale360View.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetSale360View.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.sale360ViewList = action.payload;
      })
      .addCase(GetSale360View.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetTeamTask360View.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetTeamTask360View.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.teamTask360ViewList = action.payload;
      })
      .addCase(GetTeamTask360View.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getcustomerBySource.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getcustomerBySource.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.customerBySourceList = action.payload;
      })
      .addCase(getcustomerBySource.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getcustomerByStatus.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getcustomerByStatus.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.customerByStatusList = action.payload;
      })
      .addCase(getcustomerByStatus.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getcustomerByStage.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getcustomerByStage.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.customerByStageList = action.payload;
      })
      .addCase(getcustomerByStage.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetProductOrderStatus.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetProductOrderStatus.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.productOrderStatusList = action.payload;
      })
      .addCase(GetProductOrderStatus.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
        state.data.productOrderStatusList = [];
      })
      .addCase(GetFollowUps.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetFollowUps.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.followUpsList = action.payload;
      })
      .addCase(GetFollowUps.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetTopFiveCallBy.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetTopFiveCallBy.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topCallByList = action.payload;
      })
      .addCase(GetTopFiveCallBy.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetTopFiveCallTo.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetTopFiveCallTo.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topCallToList = action.payload;
      })
      .addCase(GetTopFiveCallTo.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetBottomFiveCallBy.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetBottomFiveCallBy.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.bottomCallByList = action.payload;
      })
      .addCase(GetBottomFiveCallBy.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetBottomFiveCallTo.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetBottomFiveCallTo.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.bottomCallToList = action.payload;
      })
      .addCase(GetBottomFiveCallTo.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetTopProduct.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetTopProduct.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.topProductList = action.payload;
      })
      .addCase(GetTopProduct.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetTopMeetings.pending, (state) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetTopMeetings.fulfilled, (state, action) => {
        state.isFetching = false;
        const { DataFor } = action.meta.arg;

        switch (DataFor) {
          case "UpcomingMeeting":
            state.data.topMeetingsList = action.payload;
            break;
          case "PastMeeting":
            state.data.topPastMeetingsList = action.payload;
            break;
          default:
            break;
        }
      })
      .addCase(GetTopMeetings.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllListViewTask.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllListViewTask.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.taskViewList = action.payload;
      })
      .addCase(GetAllListViewTask.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
        state.data.taskViewList = [];
      })
      .addCase(GetIndividualTaskView.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetIndividualTaskView.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.individualTaskViewList = action.payload;
      })
      .addCase(GetIndividualTaskView.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetFeedActivityLrm.pending, (state: any) => {
        state.feedActivityFetching = true;
        state.error = undefined;
      })
      .addCase(GetFeedActivityLrm.fulfilled, (state, action) => {
        state.feedActivityFetching = false;
        state.data.feedActivityList = action.payload;
      })
      .addCase(GetFeedActivityLrm.rejected, (state: any, action) => {
        state.feedActivityFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllAttendanceData.pending, (state) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllAttendanceData.fulfilled, (state, action) => {
        state.isFetching = false;
        const { dataFor } = action.meta.arg;

        switch (dataFor) {
          case "Absentees":
            state.data.allAbsenteesList = action.payload;
            break;
          case "HalfDays":
            state.data.allhalfDayList = action.payload;
            break;
          case "ExtraDays":
            state.data.allExtraDaysList = action.payload;
            break;
          case "PlannedLeaves":
            state.data.allPlannedLeaves = action.payload;
            break;
          case "ClaimUpdates":
            state.data.allClaimUpdates = action.payload;
            break;
          default:
            break;
        }
      })
      .addCase(GetAllAttendanceData.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllLocationUsers.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllLocationUsers.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.usersByLocationList = action.payload;
      })
      .addCase(GetAllLocationUsers.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllExpense.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllExpense.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.allExpenseList = action.payload;
      })
      .addCase(GetAllExpense.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllCustomerViewReport.pending, (state) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllCustomerViewReport.fulfilled, (state, action) => {
        state.isFetching = false;
        const { dataFor } = action.meta.arg;

        switch (dataFor) {
          case "CustomerAdded":
            state.data.customerAddedList = action.payload;
            break;
          case "CustomerVisit":
            state.data.customerVisitList = action.payload;
            break;
          case "CustomerDeal":
            state.data.customerDealList = action.payload;
            break;
          case "CustomersDealsWon":
            state.data.customerDealsWonList = action.payload;
            break;
          case "CustomerOrder":
            state.data.customerOrderList = action.payload;
            break;
          case "UpcomingMeeting":
            state.data.upcomingMeetingList = action.payload;
            break;
          case "PastMeeting":
            state.data.pastMeetingList = action.payload;
            break;
          case "ProductList":
            state.data.productList = action.payload;
            break;
          case "OrderStatusView":
            state.data.orderStatusViewList = action.payload;
            break;
          case "LeadBySourceList":
            state.data.leadBySourceList = action.payload;
            break;
          case "LeadByStageList":
            state.data.leadByStageList = action.payload;
            break;
          case "LeadByStatusList":
            state.data.leadByStatusList = action.payload;
            break;
          default:
            break;
        }
      })
      .addCase(GetAllCustomerViewReport.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllCall.pending, (state) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllCall.fulfilled, (state, action) => {
        state.isFetching = false;
        const { dataFor } = action.meta.arg;

        switch (dataFor) {
          case "ListViewTopCallBy":
            state.data.topCallByListAll = action.payload;
            break;
          case "ListViewTopCallTo":
            state.data.topCallToListAll = action.payload;
            break;
          case "ListViewBottomCallBy":
            state.data.bottomCallByListAll = action.payload;
            break;
          case "ListViewBottomCallTo":
            state.data.bottomCallToListAll = action.payload;
            break;
          default:
            break;
        }
      })
      .addCase(GetAllCall.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllDealPipeline.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllDealPipeline.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.dealsAndPipelinesListAll = action.payload;
      })
      .addCase(GetAllDealPipeline.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetLeadConversionAllCustomers.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetLeadConversionAllCustomers.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.leadConversionAllCustomersList = action.payload;
      })
      .addCase(GetLeadConversionAllCustomers.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetConvertedLeads.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetConvertedLeads.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.convertedLeadsList = action.payload;
      })
      .addCase(GetConvertedLeads.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetConvertedOpportunity.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetConvertedOpportunity.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.convertedOpportunityList = action.payload;
      })
      .addCase(GetConvertedOpportunity.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(GetAllOpportunity.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(GetAllOpportunity.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.allOpportunityList = action.payload;
      })
      .addCase(GetAllOpportunity.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getLeadTransaction.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLeadTransaction.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.leadTransactionList = action.payload;
      })
      .addCase(getLeadTransaction.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getLeadStatus.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLeadStatus.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.leadStatusList = action.payload;
      })
      .addCase(getLeadStatus.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getVisitPurposes.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getVisitPurposes.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.visitPurposesList = action.payload;
      })
      .addCase(getVisitPurposes.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getVisitsMetrics.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getVisitsMetrics.fulfilled, (state, action) => {
        state.isFetching = false;
        state.data.metricsData = action.payload;
      })
      .addCase(getVisitsMetrics.rejected, (state: any, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getOpportunityMetrics.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getOpportunityMetrics.fulfilled, (state: { isFetching: boolean; data: { opportunitymetricsData: any; }; }, action: { payload: any; }) => {
        state.isFetching = false;
        state.data.opportunitymetricsData = action.payload;
      })
      .addCase(getOpportunityMetrics.rejected, (state: any, action: { payload: any; }) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getOrderMetrics.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getOrderMetrics.fulfilled, (state: { isFetching: boolean; data: { ordermetricsData: any; }; }, action: { payload: any; }) => {
        state.isFetching = false;
        state.data.ordermetricsData = action.payload;
      })
      .addCase(getOrderMetrics.rejected, (state: any, action: { payload: any; }) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getMostOrderedProducts.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getMostOrderedProducts.fulfilled, (state: { isFetching: boolean; data: { mostOrderedProductList: any; }; }, action: { payload: any; }) => {
        state.isFetching = false;
        state.data.mostOrderedProductList = action.payload;
      })
      .addCase(getMostOrderedProducts.rejected, (state: any, action: { payload: any; }) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getTaskMetrics.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getTaskMetrics.fulfilled, (state: { isFetching: boolean; data: { taskMetricsData: any; }; }, action: { payload: any; }) => {
        state.isFetching = false;
        state.data.taskMetricsData = action.payload;
      })
      .addCase(getTaskMetrics.rejected, (state: any, action: { payload: any; }) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getReferralBussiness.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getReferralBussiness.fulfilled, (state: { isFetching: boolean; data: { referralList: any; }; }, action: { payload: any; }) => {
        state.isFetching = false;
        state.data.referralList = action.payload;
      })
      .addCase(getReferralBussiness.rejected, (state: any, action: { payload: any; }) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      .addCase(getLeadOpportunityCount.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLeadOpportunityCount.fulfilled, (state: any ,action:{payload:any})=>{
        state.isFetching = false;
        state.data.leadOpportunityCount = action.payload;
      })
      .addCase(getLeadOpportunityCount.rejected, (state: any, action: { payload: any; }) => {
        state.isFetching = false;
        state.error = action.payload;
      })
       .addCase(getLeadOpportunityReport.pending, (state: any) => {
        state.isFetching = true;
        state.error = undefined;
      })
      .addCase(getLeadOpportunityReport.fulfilled, (state: any ,action:{payload:any})=>{
        state.isFetching = false;
        state.data.leadOpportunityReport = action.payload;
      })
      .addCase(getLeadOpportunityReport.rejected, (state: any, action: { payload: any; }) => {
        state.isFetching = false;
        state.error = action.payload;
      });
  },
});

export default dashboard360Slice.reducer;
